/* inkwell-game.js
 *
 * Multi-storefront game switcher for Inkwell NZ.
 *
 * One Shopify store carries several product lines. This script lets a customer
 * flip the entire storefront experience between them by toggling a single
 * value in localStorage:
 *
 *     localStorage.inkwell_game = 'lorcana' | 'pokemon' | 'magic' | 'riftbound'
 *
 * Lorcana is and stays the DEFAULT — it is the store's specialist identity and
 * its SEO. A visitor who has never touched the switcher sees exactly the store
 * that existed before this file did.
 *
 * Riftbound remains a valid GAMES value (an old localStorage value from that
 * paused build resolves rather than silently falling back to Lorcana) but has
 * no storefront surface (binned as a singles line 2026-08-29), so 2026-09-06
 * removed its entries from LINKS — those pointed at pages that don't exist
 * (/pages/riftbound, /pages/riftbound-featured-decks, etc). rewriteLinks()
 * simply leaves a Riftbound-mode shopper's authored href alone now, which is
 * safer than sending them to a 404.
 *
 * On every page load it sets <html data-game="..."> so CSS can branch on
 * `:root[data-game="pokemon"]` (e.g. swap --accent-color), and rewrites a
 * small set of game-aware nav links so clicking "Home" in Pokemon mode lands
 * on /pages/pokemon rather than "/", etc.
 *
 * Cart is shared — Shopify carts are store-wide. A Lorcana product and a
 * Pokemon product can sit side-by-side at checkout regardless of mode.
 *
 * Usage in Liquid:
 *     <a href="/" data-game-link="home">Home</a>
 *     <a href="/collections/lorcana-all" data-game-link="all">Shop all</a>
 *
 * The `data-game-link` attribute is the trigger. Plain <a href> stays as-is.
 *
 * No cookies — pure client-side, no PII, no consent flow needed.
 */
(function () {
  var KEY = 'inkwell_game';
  var DEFAULT = 'lorcana';
  var GAMES = { lorcana: true, pokemon: true, magic: true, riftbound: true };

  // Map of data-game-link values -> { <game>: '/path' }.
  //
  // A game may omit a key: rewriteLinks leaves the authored href alone when
  // there is no entry, so Magic (no storefront yet) and Riftbound (paused,
  // pages removed) simply keep whatever was already authored rather than
  // pointing at a 404. Add a key here only once the surface actually exists.
  var LINKS = {
    home: {
      lorcana: '/',
      pokemon: '/pages/pokemon'
    },
    all: {
      lorcana: '/collections/lorcana-all',
      pokemon: '/collections/pokemon-all'
    },
    decks: {
      lorcana: '/pages/featured-decks'
    },
    buylist: {
      lorcana: '/pages/buylist'
    },
    decklist: {
      lorcana: '/pages/decklist-import'
    }
  };

  // The PAGE may declare its own game, server-side, via
  //     <html data-game="pokemon" data-game-source="page">
  // (layout/theme.liquid). Since 2026-09-06 this is emitted on EVERY page
  // INCLUDING Lorcana ones (so a Google visitor landing on a Lorcana card
  // persists "lorcana" the same way a Pokemon landing persists "pokemon"),
  // with one deliberate exception: /search never declares a page game,
  // because it is the one cross-game surface (see main-search.liquid) and
  // must fall through to localStorage below instead.
  function pageDeclaredGame() {
    try {
      var el = document.documentElement;
      if (el.getAttribute('data-game-source') !== 'page') return null;
      var v = el.getAttribute('data-game');
      return GAMES[v] ? v : null;
    } catch (e) {
      return null;
    }
  }

  // An explicit ?game=<slug> in the URL — the switcher links, and the
  // "Switch to Lorcana"/"Switch back to Lorcana" links, all carry this.
  // Takes priority over the page's own declaration so a link that is
  // deliberately crossing game context (e.g. the search notice) wins.
  function urlDeclaredGame() {
    try {
      var v = new URLSearchParams(window.location.search).get('game');
      return GAMES[v] ? v : null;
    } catch (e) {
      return null;
    }
  }

  function read() {
    var fromUrl = urlDeclaredGame();
    if (fromUrl) return fromUrl;
    var declared = pageDeclaredGame();
    if (declared) return declared;
    try {
      var v = window.localStorage.getItem(KEY);
      return GAMES[v] ? v : DEFAULT;
    } catch (e) {
      return DEFAULT;
    }
  }

  function write(v) {
    if (!GAMES[v]) return;
    try { window.localStorage.setItem(KEY, v); } catch (e) {}
    document.documentElement.setAttribute('data-game', v);
    rewriteLinks(v);
    fireChange(v);
  }

  function rewriteLinks(game) {
    var nodes = document.querySelectorAll('[data-game-link]');
    for (var i = 0; i < nodes.length; i++) {
      var key = nodes[i].getAttribute('data-game-link');
      var entry = LINKS[key];
      if (entry && entry[game]) nodes[i].setAttribute('href', entry[game]);
    }
  }

  var listeners = [];
  function onChange(fn) { listeners.push(fn); }
  function fireChange(v) { for (var i = 0; i < listeners.length; i++) listeners[i](v); }

  // Apply on first paint so CSS branching is in place before sections render.
  // Runs in <head> via {{ 'inkwell-game.js' | asset_url }} script tag.
  var initial = read();
  document.documentElement.setAttribute('data-game', initial);

  // Persist whenever the game came from somewhere authoritative — a page
  // declaration or an explicit ?game= link both mean "this is now the
  // customer's experience," so remember it for the next bare "/" visit.
  // No-op on a Lorcana page with no ?game= param when localStorage already
  // said lorcana (nothing changed, nothing to write).
  if (urlDeclaredGame() || pageDeclaredGame()) {
    try { window.localStorage.setItem(KEY, initial); } catch (e) {}
  }

  // Cross-tab sync: if another tab flips the game, follow along.
  window.addEventListener('storage', function (e) {
    if (e.key === KEY && GAMES[e.newValue]) {
      document.documentElement.setAttribute('data-game', e.newValue);
      rewriteLinks(e.newValue);
      fireChange(e.newValue);
    }
  });

  // Rewrite nav links once DOM is ready (sections may render late).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { rewriteLinks(read()); });
  } else {
    rewriteLinks(initial);
  }

  // Public API
  window.Inkwell = window.Inkwell || {};
  window.Inkwell.getGame = read;
  window.Inkwell.setGame = write;
  window.Inkwell.onGameChange = onChange;
  window.Inkwell.GAME_LINKS = LINKS;
})();
